import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import arqeraLogo from "@/assets/arqera-logo.png";

const links = [
  { label: "Services", href: "#services", id: "services" },
  { label: "Methodology", href: "/methodology", id: "methodology", external: true as const },
  { label: "About", href: "#about", id: "about" },
  { label: "Resources", href: "/resources", id: "resources", external: true as const },
  { label: "Contact", href: "#contact", id: "contact" },
];

// [CONFIRM] Replace with real Calendly URL when available
const CALENDLY_URL: string | null = null;

export default function ArqNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState<string>("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: [0.4] }
    );
    links.forEach((l) => {
      const el = document.getElementById(l.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const goto = (href: string) => {
    setOpen(false);
    if (href.startsWith("/")) {
      window.location.href = href;
      return;
    }
    if (window.location.pathname !== "/") {
      window.location.href = `/${href}`;
      return;
    }
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  const onScheduleClick = () => {
    if (CALENDLY_URL) {
      window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
      return;
    }
    goto("#contact");
  };

  const Logo = () => (
    <a href="#top" className="flex items-center" aria-label="ArqeraAI home">
      <img src={arqeraLogo} alt="ArqeraAI" className="h-10 md:h-12 w-auto" />
    </a>
  );

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-arq-bg/85 backdrop-blur-md border-b border-arq-border" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-6 flex items-center justify-between h-20">
        <Logo />

        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => {
            const isActive = activeSection === l.id;
            if (l.external) {
              return (
                <Link
                  key={l.href}
                  to={l.href}
                  className={`text-sm transition-colors duration-150 ${
                    isActive ? "text-arq-blue font-medium" : "text-arq-muted hover:text-arq-text font-medium"
                  }`}
                >
                  {l.label}
                </Link>
              );
            }
            return (
              <button
                key={l.href}
                onClick={() => goto(l.href)}
                className={`text-sm transition-colors duration-150 ${
                  isActive ? "text-arq-blue font-medium" : "text-arq-muted hover:text-arq-text font-medium"
                }`}
              >
                {l.label}
              </button>
            );
          })}
          <button
            onClick={onScheduleClick}
            className="bg-arq-blue text-white text-sm font-semibold px-5 py-2.5 rounded-md hover:bg-arq-blue-bright hover:shadow-[0_0_24px_rgba(26,111,212,0.35)] transition-all duration-150"
          >
            Schedule Assessment
          </button>
        </div>

        <button
          className="md:hidden text-arq-text"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-arq-bg border-t border-arq-border px-6 py-4 flex flex-col gap-4">
          {links.map((l) => {
            const isActive = activeSection === l.id;
            if (l.external) {
              return (
                <Link
                  key={l.href}
                  to={l.href}
                  onClick={() => setOpen(false)}
                  className={`text-left text-base font-medium ${
                    isActive ? "text-arq-blue" : "text-arq-muted hover:text-arq-text"
                  }`}
                >
                  {l.label}
                </Link>
              );
            }
            return (
              <button
                key={l.href}
                onClick={() => goto(l.href)}
                className={`text-left text-base font-medium ${
                  isActive ? "text-arq-blue" : "text-arq-muted hover:text-arq-text"
                }`}
              >
                {l.label}
              </button>
            );
          })}
          <button
            onClick={onScheduleClick}
            className="w-full bg-arq-blue text-white text-sm font-semibold px-5 py-3 rounded-md"
          >
            Schedule Assessment
          </button>
        </div>
      )}
    </nav>
  );
}
